/**
 * Image Inspector Modal (Bild-Popup)
 *
 * Central review & refine popup (briefing §4), opened from the workspace
 * gallery and the generation wizard:
 * - big image + variant strip (Original · Leer A/B · Eingerichtet …)
 * - inner arrows cycle variants, outer arrows jump between rooms
 * - right panel: rename, room type, Küche behalten, Anpassen,
 *   Neu generieren (cost shown live from credit_pricing), download/delete.
 *   (Furnishing happens exclusively via the wizard's Einrichten step.)
 *
 * Self-contained: fetches the images row + image_history itself and refetches
 * after every mutation. Parent is notified via onChanged/onCreditsRefresh.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  Trash2,
  Loader2,
  Wand2,
  RefreshCw,
  Check,
  AlertTriangle,
  Pencil,
  Image as ImageIcon,
  ShieldCheck,
  ScanLine,
} from 'lucide-react';
import { saveAs } from 'file-saver';
import { RoomType, GenerationType, ImageHistoryEntry, StagingStyle, BoxCoordinates } from '@/types';
import {
  getIndoorRooms,
  getOutdoorRooms,
  getRoomLabel,
  supportsStaging,
} from '@/lib/roomConfig';
import { generateRoomImage, requestStageRefinement } from '@/services/geminiService';
import {
  fetchImageDetail,
  fetchImageHistoryEntries,
  updateImageMetadata,
  deleteImageVariant,
  setCurrentVariant,
  deleteImageWithHistory,
  getPublicUrl,
} from '@/services/projectService';
import type { ImageDetailRow } from '@/services/projectService';
import { useAuth } from '@/context/AuthContext';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useCreditPricing } from '@/hooks/useCreditPricing';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { TimedProgress } from '@/components/ui/TimedProgress';
import { RegionSelector } from '@/components/ui/RegionSelector';
import { cn } from '@/lib/utils';
import { useUnloadWarning } from '@/hooks/useUnloadWarning';

// ============================================================================
// Contract (frozen — App.tsx and ProjectWizardView are wired against this)
// ============================================================================

export interface InspectorRoom {
  imageId: string;
  name: string;
  roomType: import('@/types').RoomType;
  keepKitchen: boolean;
  originalUrl: string | null;
  originalPath: string | null;
}

export interface ImageInspectorModalProps {
  open: boolean;
  rooms: InspectorRoom[];
  index: number;
  projectId: string;
  stagingStyle: 'modern' | 'luxury' | 'industrial';
  aspectRatio: '1:1' | '4:3';
  isAdmin: boolean;
  /** Aufruf-Kontext: im Wizard werden Download/Löschen der Variante ausgeblendet. */
  context: 'wizard' | 'workspace';
  onIndexChange: (i: number) => void;
  onClose: () => void;
  onChanged: () => void;
  onCreditsRefresh: () => void;
}

// ============================================================================
// Internal types
// ============================================================================

type VariantKind = 'original' | 'empty' | 'staged' | 'branded';

interface VariantItem {
  id: string;
  kind: VariantKind;
  label: string;
  storagePath: string;
  url: string;
  historyId: string | null;
  /** True if this variant is the current storage_path_empty/staged on the images row. */
  isCurrent: boolean;
}

type BusyAction = 'refine' | 'regen' | 'deleteVariant' | 'deleteImage' | 'meta' | null;

interface LoadedData {
  detail: ImageDetailRow | null;
  history: ImageHistoryEntry[];
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
};

// ============================================================================
// Component
// ============================================================================

export const ImageInspectorModal: React.FC<ImageInspectorModalProps> = ({
  open,
  rooms,
  index,
  projectId,
  stagingStyle,
  aspectRatio,
  isAdmin,
  context,
  onIndexChange,
  onClose,
  onChanged,
  onCreditsRefresh,
}) => {
  const { user } = useAuth();
  const { settings } = useAppSettings();
  const { pricing } = useCreditPricing();

  // Feature flags (admin/live split, mirrors the old RegenerationModal / ProjectDetailsView).
  // stage_refinement → box-editor refinement path; else legacy whole-image refine.
  const refineActive = isAdmin ? settings.stage_refinement_admin : settings.stage_refinement_live;
  const catalogActive = isAdmin ? settings.catalog_staging_admin : settings.catalog_staging_live;

  const room = rooms[index] as InspectorRoom | undefined;
  const imageId = room?.imageId ?? null;

  // Data
  const [detail, setDetail] = useState<ImageDetailRow | null>(null);
  const [history, setHistory] = useState<ImageHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // UI state
  const [activeId, setActiveId] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState<BusyAction>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [regenAdvice, setRegenAdvice] = useState<{ reason: string | null } | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [deleteImageArmed, setDeleteImageArmed] = useState(false);
  // Region box for the Box-Editor refinement (only used when refineActive).
  const [boxCoords, setBoxCoords] = useState<BoxCoordinates | null>(null);

  const isGenerating = busy === 'refine' || busy === 'regen';

  // Reload / Tab schließen während laufender Generierung abfangen.
  useUnloadWarning(isGenerating);

  // ── Data loading ──────────────────────────────────────────────────────────

  const loadData = useCallback(async (): Promise<LoadedData | null> => {
    if (!imageId) return null;
    setIsLoading(true);
    setLoadError(null);
    try {
      const [d, h] = await Promise.all([
        fetchImageDetail(imageId),
        fetchImageHistoryEntries(imageId),
      ]);
      setDetail(d);
      setHistory(h);
      return { detail: d, history: h };
    } catch (err) {
      console.error('[Inspector] Failed to load image data:', err);
      setLoadError('Bilddaten konnten nicht geladen werden.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [imageId]);

  // Reset + reload whenever the popup opens or the room changes
  useEffect(() => {
    if (!open || !imageId) return;
    setDetail(null);
    setHistory([]);
    setActiveId(null);
    setPrompt('');
    setActionError(null);
    setRegenAdvice(null);
    setIsRenaming(false);
    setDeleteImageArmed(false);
    setBoxCoords(null);
    void loadData();
  }, [open, imageId, loadData]);

  // Auto-disarm the two-step image delete (like the prototype)
  useEffect(() => {
    if (!deleteImageArmed) return;
    const t = window.setTimeout(() => setDeleteImageArmed(false), 3000);
    return () => window.clearTimeout(t);
  }, [deleteImageArmed]);

  // ── Variants ──────────────────────────────────────────────────────────────

  const variants = useMemo<VariantItem[]>(() => {
    const list: VariantItem[] = [];

    const originalPath = detail?.storage_path ?? room?.originalPath ?? null;
    const originalUrl = originalPath ? getPublicUrl(originalPath) : room?.originalUrl ?? null;
    if (originalUrl) {
      list.push({
        id: 'original',
        kind: 'original',
        label: 'Original',
        storagePath: originalPath ?? '',
        url: originalUrl,
        historyId: null,
        isCurrent: true,
      });
    }

    const buildGroup = (
      kind: 'empty' | 'staged',
      currentPath: string | null,
      labelFor: (i: number) => string
    ): VariantItem[] => {
      const versionType = kind === 'empty' ? 'Empty' : 'Staged';
      const rows = history.filter((h) => h.version_type === versionType);
      const items: VariantItem[] = rows.map((r) => ({
        id: r.id,
        kind,
        label: '',
        storagePath: r.storage_path,
        url: getPublicUrl(r.storage_path),
        historyId: r.id,
        isCurrent: !!currentPath && r.storage_path === currentPath,
      }));
      // Current column value not covered by history (e.g. parallel variants
      // where only one history row was recorded) → synthetic variant.
      if (currentPath && !rows.some((r) => r.storage_path === currentPath)) {
        items.unshift({
          id: `current-${kind}`,
          kind,
          label: '',
          storagePath: currentPath,
          url: getPublicUrl(currentPath),
          historyId: null,
          isCurrent: true,
        });
      }
      items.forEach((v, i) => {
        v.label = labelFor(i);
      });
      return items;
    };

    const emptyItems = buildGroup('empty', detail?.storage_path_empty ?? null, (i) =>
      i === 0 ? 'Leer A' : i === 1 ? 'Leer B' : `Leer ${i + 1}`
    );
    const stagedItems = buildGroup('staged', detail?.storage_path_staged ?? null, (i) =>
      i === 0 ? 'Eingerichtet' : `Eingerichtet ${i + 1}`
    );

    // Gebrandete Bilder (persistiert vom Wizard-Export) — reine Ansicht/Download,
    // kein history-Eintrag, nie "current".
    const brandedItems: VariantItem[] = [];
    if (detail?.storage_path_branded_empty) {
      brandedItems.push({
        id: 'branded-empty',
        kind: 'branded',
        label: 'Gebrandet · Leer',
        storagePath: detail.storage_path_branded_empty,
        url: getPublicUrl(detail.storage_path_branded_empty),
        historyId: null,
        isCurrent: false,
      });
    }
    if (detail?.storage_path_branded) {
      brandedItems.push({
        id: 'branded-staged',
        kind: 'branded',
        label: 'Gebrandet · Eingerichtet',
        storagePath: detail.storage_path_branded,
        url: getPublicUrl(detail.storage_path_branded),
        historyId: null,
        isCurrent: false,
      });
    }

    return [...list, ...emptyItems, ...stagedItems, ...brandedItems];
  }, [detail, history, room]);

  const activeVariant = useMemo<VariantItem | null>(() => {
    if (activeId) {
      const found = variants.find((v) => v.id === activeId);
      if (found) return found;
    }
    const staged = variants.filter((v) => v.kind === 'staged');
    const currentStaged = staged.find((v) => v.isCurrent) ?? staged[staged.length - 1];
    if (currentStaged) return currentStaged;
    const empty = variants.filter((v) => v.kind === 'empty');
    const currentEmpty = empty.find((v) => v.isCurrent) ?? empty[empty.length - 1];
    if (currentEmpty) return currentEmpty;
    return variants[0] ?? null;
  }, [variants, activeId]);

  // The region box is anchored to the currently shown variant image — drop it on switch.
  useEffect(() => {
    setBoxCoords(null);
  }, [activeVariant?.id]);

  const hasEmpty = variants.some((v) => v.kind === 'empty');
  const hasStaged = variants.some((v) => v.kind === 'staged');
  const generatedCount = variants.filter(
    (v) => v.kind !== 'original' && v.kind !== 'branded'
  ).length;

  const currentType = detail?.room_type ?? String(room?.roomType ?? RoomType.CUSTOM);
  const displayName = detail?.custom_label || room?.name || getRoomLabel(currentType);
  const stageable = supportsStaging(currentType);

  const statusLine = useMemo(() => {
    const parts: string[] = [];
    if (hasEmpty) parts.push('Leer');
    if (hasStaged) parts.push('Eingerichtet');
    if (parts.length === 0) return 'Nur Original';
    return `${parts.join(' + ')} · ${generatedCount} ${generatedCount === 1 ? 'Version' : 'Versionen'}`;
  }, [hasEmpty, hasStaged, generatedCount]);

  // ── Navigation ────────────────────────────────────────────────────────────

  const goRoom = useCallback(
    (dir: -1 | 1) => {
      if (rooms.length < 2 || isGenerating) return;
      onIndexChange((index + dir + rooms.length) % rooms.length);
    },
    [rooms.length, index, onIndexChange, isGenerating]
  );

  const cycleVariant = useCallback(
    (dir: -1 | 1) => {
      if (variants.length < 2 || !activeVariant) return;
      const i = variants.findIndex((v) => v.id === activeVariant.id);
      const next = variants[(i + dir + variants.length) % variants.length];
      if (next) setActiveId(next.id);
    },
    [variants, activeVariant]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (!isGenerating) onClose();
        return;
      }
      if (isEditableTarget(e.target)) return;
      if (e.key === 'ArrowLeft') goRoom(-1);
      if (e.key === 'ArrowRight') goRoom(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, goRoom, isGenerating]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const selectNewestOfKind = useCallback((data: LoadedData | null, kind: 'empty' | 'staged') => {
    if (!data) return;
    const versionType = kind === 'empty' ? 'Empty' : 'Staged';
    const rows = data.history.filter((h) => h.version_type === versionType);
    const newest = rows[rows.length - 1];
    if (newest) {
      setActiveId(newest.id);
    } else {
      setActiveId(`current-${kind}`);
    }
  }, []);

  const errorMessage = (err: unknown): string =>
    err instanceof Error ? err.message : 'Unbekannter Fehler';

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleRefine = useCallback(
    async (forceEdit: boolean) => {
      if (!imageId || !activeVariant) return;
      if (activeVariant.kind === 'original' || activeVariant.kind === 'branded') return;
      const text = prompt.trim();
      if (!text) return;
      setBusy('refine');
      setActionError(null);
      try {
        const kind = activeVariant.kind; // 'empty' | 'staged'
        const versionType = kind === 'empty' ? 'Empty' : 'Staged';

        if (refineActive) {
          // Box-Editor refinement path (feature flag on).
          const result = await requestStageRefinement({
            storagePath: activeVariant.storagePath,
            imageDbId: imageId,
            versionType,
            userPrompt: text,
            boxCoords: boxCoords ?? undefined,
            forceEdit: forceEdit || undefined,
          });
          if (result.regenerate_suggested) {
            setRegenAdvice({ reason: result.regenerate_reason ?? null });
            return;
          }
        } else {
          // Flag off → legacy whole-image refinement via generate-stage (feedback path),
          // mirroring ProjectDetailsView's fallback. No box editor in this mode.
          const originalPath = detail?.storage_path ?? room?.originalPath;
          if (!originalPath) {
            setActionError('Kein Originalbild vorhanden — Anpassung nicht möglich.');
            return;
          }
          const isStaged = kind === 'staged';
          await generateRoomImage(
            originalPath,
            projectId,
            currentType,
            isStaged ? GenerationType.STAGED : GenerationType.EMPTY,
            detail?.keep_kitchen ?? room?.keepKitchen ?? false,
            text, // feedback
            detail?.custom_label ?? undefined,
            imageId,
            activeVariant.storagePath, // sourceImagePath — refine on top of this variant
            user?.id,
            aspectRatio,
            isStaged ? stagingStyle : undefined,
            undefined, // adminTest
            1, // variantCount: single re-roll
            isStaged && catalogActive ? detail?.storage_path_empty ?? undefined : undefined
          );
        }

        setPrompt('');
        setRegenAdvice(null);
        setBoxCoords(null);
        const fresh = await loadData();
        selectNewestOfKind(fresh, kind);
        onChanged();
        onCreditsRefresh();
      } catch (err) {
        console.error('[Inspector] Refinement failed:', err);
        setActionError(errorMessage(err));
      } finally {
        setBusy(null);
      }
    },
    [
      imageId,
      activeVariant,
      prompt,
      refineActive,
      boxCoords,
      catalogActive,
      detail,
      room,
      projectId,
      currentType,
      user?.id,
      aspectRatio,
      stagingStyle,
      loadData,
      selectNewestOfKind,
      onChanged,
      onCreditsRefresh,
    ]
  );

  const runGeneration = useCallback(
    async (kind: 'empty' | 'staged', style: StagingStyle) => {
      if (!imageId || !room) return;
      const originalPath = detail?.storage_path ?? room.originalPath;
      if (!originalPath) {
        setActionError('Kein Originalbild vorhanden — Generierung nicht möglich.');
        return;
      }
      setBusy('regen');
      setActionError(null);
      setRegenAdvice(null);
      try {
        const genType = kind === 'empty' ? GenerationType.EMPTY : GenerationType.STAGED;
        await generateRoomImage(
          originalPath,
          projectId,
          currentType,
          genType,
          detail?.keep_kitchen ?? room.keepKitchen ?? false,
          undefined, // feedback
          detail?.custom_label ?? undefined,
          imageId,
          undefined, // sourceImagePath
          user?.id,
          aspectRatio,
          style,
          undefined, // adminTest
          1, // variantCount
          genType === GenerationType.STAGED
            ? detail?.storage_path_empty ?? undefined
            : undefined
        );
        const fresh = await loadData();
        selectNewestOfKind(fresh, kind);
        onChanged();
        onCreditsRefresh();
      } catch (err) {
        console.error('[Inspector] Generation failed:', err);
        setActionError(errorMessage(err));
      } finally {
        setBusy(null);
      }
    },
    [
      imageId,
      room,
      detail,
      projectId,
      currentType,
      user?.id,
      aspectRatio,
      loadData,
      selectNewestOfKind,
      onChanged,
      onCreditsRefresh,
    ]
  );

  const handleRegenerate = useCallback(() => {
    if (!activeVariant || activeVariant.kind === 'original' || activeVariant.kind === 'branded') return;
    void runGeneration(activeVariant.kind, stagingStyle);
  }, [activeVariant, runGeneration, stagingStyle]);

  const handleDownload = useCallback(async () => {
    if (!activeVariant) return;
    try {
      const response = await fetch(activeVariant.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const safeName = `${displayName}_${activeVariant.label}`.replace(/\s+/g, '_');
      saveAs(blob, `${safeName}.png`);
    } catch (err) {
      console.error('[Inspector] Download failed:', err);
      setActionError('Download fehlgeschlagen. Bitte erneut versuchen.');
    }
  }, [activeVariant, displayName]);

  const handleDeleteVariant = useCallback(async () => {
    if (!imageId || !activeVariant) return;
    if (activeVariant.kind === 'original' || activeVariant.kind === 'branded') return;
    setBusy('deleteVariant');
    setActionError(null);
    try {
      await deleteImageVariant({
        imageId,
        versionType: activeVariant.kind === 'empty' ? 'Empty' : 'Staged',
        storagePath: activeVariant.storagePath,
        historyId: activeVariant.historyId,
      });
      setActiveId(null);
      await loadData();
      onChanged();
    } catch (err) {
      console.error('[Inspector] Delete variant failed:', err);
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }, [imageId, activeVariant, loadData, onChanged]);

  const handleDeleteImage = useCallback(async () => {
    if (!imageId) return;
    if (!deleteImageArmed) {
      setDeleteImageArmed(true);
      return;
    }
    setBusy('deleteImage');
    setActionError(null);
    try {
      await deleteImageWithHistory(imageId);
      onChanged();
      onClose();
    } catch (err) {
      console.error('[Inspector] Delete image failed:', err);
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
      setDeleteImageArmed(false);
    }
  }, [imageId, deleteImageArmed, onChanged, onClose]);

  const handleSetCurrentEmpty = useCallback(async () => {
    if (!imageId || !activeVariant || activeVariant.kind !== 'empty' || activeVariant.isCurrent) return;
    setBusy('meta');
    setActionError(null);
    try {
      await setCurrentVariant(imageId, 'Empty', activeVariant.storagePath);
      await loadData();
      onChanged();
    } catch (err) {
      console.error('[Inspector] Set current empty failed:', err);
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }, [imageId, activeVariant, loadData, onChanged]);

  const handleSetCurrentStaged = useCallback(async () => {
    if (!imageId || !activeVariant || activeVariant.kind !== 'staged' || activeVariant.isCurrent) return;
    setBusy('meta');
    setActionError(null);
    try {
      await setCurrentVariant(imageId, 'Staged', activeVariant.storagePath);
      await loadData();
      onChanged();
    } catch (err) {
      console.error('[Inspector] Set current staged failed:', err);
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }, [imageId, activeVariant, loadData, onChanged]);

  const handleRenameSave = useCallback(async () => {
    if (!imageId) return;
    const trimmed = nameDraft.trim();
    setIsRenaming(false);
    if (!trimmed || trimmed === displayName) return;
    setBusy('meta');
    setActionError(null);
    try {
      await updateImageMetadata(imageId, { custom_label: trimmed });
      setDetail((prev) => (prev ? { ...prev, custom_label: trimmed } : prev));
      onChanged();
    } catch (err) {
      console.error('[Inspector] Rename failed:', err);
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }, [imageId, nameDraft, displayName, onChanged]);

  const handleTypeChange = useCallback(
    async (newType: string) => {
      if (!imageId || newType === currentType) return;
      setBusy('meta');
      setActionError(null);
      try {
        await updateImageMetadata(imageId, { room_type: newType });
        setDetail((prev) => (prev ? { ...prev, room_type: newType } : prev));
        onChanged();
      } catch (err) {
        console.error('[Inspector] Room type change failed:', err);
        setActionError(errorMessage(err));
      } finally {
        setBusy(null);
      }
    },
    [imageId, currentType, onChanged]
  );

  const handleToggleKitchen = useCallback(async () => {
    if (!imageId) return;
    const next = !(detail?.keep_kitchen ?? room?.keepKitchen ?? false);
    setBusy('meta');
    setActionError(null);
    try {
      await updateImageMetadata(imageId, { keep_kitchen: next });
      setDetail((prev) => (prev ? { ...prev, keep_kitchen: next } : prev));
      onChanged();
    } catch (err) {
      console.error('[Inspector] Keep-kitchen toggle failed:', err);
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }, [imageId, detail, room, onChanged]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (!open || !room) return null;

  const keepKitchenOn = detail?.keep_kitchen ?? room.keepKitchen ?? false;
  // Gebrandete Varianten sind reine Ansicht/Download — kein Anpassen/Neu-Generieren.
  const showAdjust =
    !!activeVariant && activeVariant.kind !== 'original' && activeVariant.kind !== 'branded';
  const busyLabel = busy === 'refine' ? 'Wird angepasst …' : 'KI generiert …';

  // Live cost for Anpassen/Neu generieren on the active variant. Both re-roll an
  // existing image → refinement pricing (credit_pricing, admin-editable). Shown
  // dynamically so the label never contradicts the actual charge.
  const refineCost =
    activeVariant && (activeVariant.kind === 'empty' || activeVariant.kind === 'staged')
      ? pricing.staging[activeVariant.kind === 'empty' ? 'Empty' : 'Staged'].refinement
      : 0;
  const costSuffix =
    refineCost === 0
      ? 'kostenlos'
      : `${refineCost.toLocaleString('de-DE')} Credit${refineCost === 1 ? '' : 's'}`;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-10"
      onClick={() => {
        if (!isGenerating) onClose();
      }}
    >
      {/* Outer room navigation */}
      {rooms.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              goRoom(-1);
            }}
            disabled={isGenerating}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur-sm transition-colors disabled:opacity-40"
            title="Vorheriger Raum"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              goRoom(1);
            }}
            disabled={isGenerating}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur-sm transition-colors disabled:opacity-40"
            title="Nächster Raum"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col lg:flex-row animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Image column ── */}
        <div className="flex-1 min-w-0 bg-slate-50 p-4 flex flex-col overflow-y-auto">
          <div
            className={cn(
              'relative mx-auto w-full rounded-xl overflow-hidden bg-slate-200 shadow-inner',
              aspectRatio === '1:1' ? 'aspect-square max-h-[62vh]' : 'aspect-[4/3] max-h-[62vh]'
            )}
          >
            {isLoading && !activeVariant ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
              </div>
            ) : loadError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-500 text-sm">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
                {loadError}
              </div>
            ) : activeVariant ? (
              <img
                src={activeVariant.url}
                alt={`${displayName} – ${activeVariant.label}`}
                className={cn('w-full h-full object-contain', isGenerating && 'opacity-40')}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <ImageIcon className="w-16 h-16 text-slate-400" />
              </div>
            )}

            {/* Box-Editor — draw the change region directly on the big preview
                (only when the Staging-Refinement flag is on for this user). */}
            {refineActive && showAdjust && activeVariant && !isGenerating && (
              <RegionSelector
                overlayOnly
                imageUrl={activeVariant.url}
                box={boxCoords}
                onBoxChange={(b) => {
                  setBoxCoords(b);
                  if (regenAdvice) setRegenAdvice(null);
                }}
                disabled={busy !== null}
              />
            )}

            {/* Generation overlay: zeitgesteuerter Fortschrittsbalken (~60 s Durchschnitt) */}
            {isGenerating && <TimedProgress label={`${busyLabel} (~60 s)`} />}

            {/* Variant badge */}
            {activeVariant && !isGenerating && (
              <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/50 text-white text-xs font-semibold backdrop-blur-sm">
                {activeVariant.label}
              </div>
            )}

            {/* Inner variant arrows */}
            {variants.length > 1 && !isGenerating && (
              <>
                <button
                  onClick={() => cycleVariant(-1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
                  title="Vorherige Variante"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => cycleVariant(1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
                  title="Nächste Variante"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Variant strip */}
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {variants.map((v) => (
              <button
                key={v.id}
                onClick={() => setActiveId(v.id)}
                disabled={isGenerating}
                className={cn(
                  'relative flex-shrink-0 w-24 rounded-lg overflow-hidden border-2 transition-all disabled:opacity-50',
                  activeVariant?.id === v.id
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-slate-200 hover:border-slate-300'
                )}
              >
                <div className="aspect-[4/3] bg-slate-200">
                  <img src={v.url} alt={v.label} className="w-full h-full object-cover" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 text-[10px] text-white text-center font-medium truncate">
                  {v.label}
                </div>
                {v.kind === 'empty' && v.isCurrent && (
                  <div
                    className="absolute top-1 right-1 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center"
                    title="Aktive Leer-Variante (Basis fürs Einrichten)"
                  >
                    <Check className="w-2.5 h-2.5 text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="w-full lg:w-[340px] flex-shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 flex flex-col overflow-y-auto">
          {/* Header */}
          <div className="p-4 border-b border-slate-200 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              {isRenaming ? (
                <input
                  autoFocus
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={() => void handleRenameSave()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    if (e.key === 'Escape') {
                      setNameDraft(displayName);
                      setIsRenaming(false);
                    }
                  }}
                  className="w-full text-lg font-bold text-slate-900 border-2 border-primary rounded-lg px-2 py-0.5 focus:outline-none"
                />
              ) : (
                <button
                  onClick={() => {
                    setNameDraft(displayName);
                    setIsRenaming(true);
                  }}
                  className="group flex items-center gap-1.5 text-left"
                  title="Zum Umbenennen klicken"
                >
                  <h2 className="text-lg font-bold text-slate-900 truncate">{displayName}</h2>
                  <Pencil className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </button>
              )}
              <p className="text-xs text-slate-500 mt-0.5">{statusLine}</p>
            </div>
            <button
              onClick={onClose}
              disabled={isGenerating}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              title={isGenerating ? 'Generierung läuft – bitte warten' : 'Schließen (Esc)'}
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {isGenerating && (
            <div className="mx-4 mt-3 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs font-medium text-foreground">
              <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-primary" />
              Generierung läuft — das Fenster bleibt offen, bis sie fertig ist.
            </div>
          )}

          <div className="p-4 space-y-4 flex-1">
            {/* Room type */}
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">Raumtyp</label>
              <select
                value={currentType}
                onChange={(e) => void handleTypeChange(e.target.value)}
                disabled={busy !== null}
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
              >
                <optgroup label="Innenbereich">
                  {getIndoorRooms().map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Außenbereich">
                  {getOutdoorRooms().map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </optgroup>
              </select>
              {!stageable && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-2">
                  „{getRoomLabel(currentType)}“ unterstützt kein Einrichten — nur Leerräumen.
                </p>
              )}
            </div>

            {/* Küche behalten */}
            {currentType === 'kitchen' && (
              <button
                onClick={() => void handleToggleKitchen()}
                disabled={busy !== null}
                className={cn(
                  'inline-flex items-center gap-1.5 text-xs font-semibold rounded-full border px-3 py-1.5 transition-colors disabled:opacity-50',
                  keepKitchenOn
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                )}
              >
                {keepKitchenOn && <Check className="w-3.5 h-3.5" />}
                Küche behalten
              </button>
            )}

            {/* Set active empty variant */}
            {activeVariant?.kind === 'empty' && !activeVariant.isCurrent && (
              <div className="space-y-1">
                <Button
                  variant="outline"
                  onClick={() => void handleSetCurrentEmpty()}
                  disabled={busy !== null}
                  className="w-full gap-2 text-sm"
                >
                  <Check className="w-4 h-4" />
                  Als Leer-Variante verwenden
                </Button>
                <p className="text-[11px] text-slate-500">
                  Diese Variante wird als Basis fürs Einrichten genutzt.
                </p>
              </div>
            )}

            {/* Set active staged variant (the one that gets exported) */}
            {activeVariant?.kind === 'staged' && !activeVariant.isCurrent && (
              <div className="space-y-1">
                <Button
                  variant="outline"
                  onClick={() => void handleSetCurrentStaged()}
                  disabled={busy !== null}
                  className="w-full gap-2 text-sm"
                >
                  <Check className="w-4 h-4" />
                  Als Eingerichtet-Variante verwenden
                </Button>
                <p className="text-[11px] text-slate-500">
                  Diese Variante wird beim Export verwendet.
                </p>
              </div>
            )}

            {/* Anpassen / Neu generieren */}
            {showAdjust && (
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-600 block">
                  Nicht perfekt? Sag der KI, was anders sein soll.
                </label>

                {/* Box-Editor guidance — the drawing surface itself is the big preview
                    on the left (only when the Staging-Refinement flag is on). */}
                {refineActive && activeVariant && (
                  <div className="space-y-1.5">
                    {boxCoords ? (
                      <p className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                        <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                        Der markierte Bereich wird als Ortsangabe an die KI übergeben.
                      </p>
                    ) : (
                      <p className="flex items-center gap-1.5 text-xs text-slate-500">
                        <ScanLine className="w-3.5 h-3.5 shrink-0" />
                        Zieh im Bild links einen Rahmen um den Bereich, den du ändern willst — optional.
                      </p>
                    )}
                    {boxCoords && (
                      <button
                        type="button"
                        onClick={() => setBoxCoords(null)}
                        disabled={busy !== null}
                        className="flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <X className="w-3 h-3" />
                        Auswahl entfernen
                      </button>
                    )}
                  </div>
                )}

                <Textarea
                  value={prompt}
                  onChange={(e) => {
                    setPrompt(e.target.value);
                    if (regenAdvice) setRegenAdvice(null);
                  }}
                  placeholder="Was soll anders sein? z. B. „hellere Couch, mehr Pflanzen“"
                  disabled={busy !== null}
                  className="min-h-[72px] text-sm resize-none"
                />

                {regenAdvice && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <div className="flex items-start gap-2 text-xs text-amber-800">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        Für diese Änderung empfiehlt sich eine <b>Neu-Generierung</b> statt einer
                        Anpassung.
                        {isAdmin && regenAdvice.reason && (
                          <span className="block mt-1 italic text-amber-700">{regenAdvice.reason}</span>
                        )}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleRegenerate}
                        disabled={busy !== null}
                        className="flex-1 text-xs"
                      >
                        Neu generieren ({costSuffix})
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleRefine(true)}
                        disabled={busy !== null}
                        className="flex-1 text-xs"
                      >
                        Trotzdem anpassen
                      </Button>
                    </div>
                  </div>
                )}

                {!regenAdvice && (
                  <div className="grid grid-cols-1 gap-2">
                    <Button
                      onClick={() => void handleRefine(false)}
                      disabled={busy !== null || !prompt.trim()}
                      className="w-full gap-1.5 text-sm"
                    >
                      {busy === 'refine' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Wand2 className="w-4 h-4" />
                      )}
                      Anpassen ({costSuffix})
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleRegenerate}
                      disabled={busy !== null}
                      className="w-full gap-1.5 text-sm"
                      title="Neue Variante — die alte bleibt erhalten"
                    >
                      {busy === 'regen' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Neu generieren ({costSuffix})
                    </Button>
                  </div>
                )}
              </div>
            )}

            {actionError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                {actionError}
              </p>
            )}
          </div>

          {/* Footer actions */}
          <div className="p-4 border-t border-slate-200 space-y-2">
            {context === 'workspace' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => void handleDownload()}
                  disabled={!activeVariant || isGenerating}
                  className="w-full gap-2 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Diese Variante herunterladen
                </Button>
                {activeVariant &&
                  activeVariant.kind !== 'original' &&
                  activeVariant.kind !== 'branded' && (
                  <Button
                    variant="outline"
                    onClick={() => void handleDeleteVariant()}
                    disabled={busy !== null}
                    className="w-full gap-2 text-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  >
                    {busy === 'deleteVariant' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Diese Variante löschen
                  </Button>
                )}
              </>
            )}
            <button
              onClick={() => void handleDeleteImage()}
              disabled={busy !== null && busy !== 'deleteImage'}
              className={cn(
                'w-full text-center text-xs py-1.5 transition-colors disabled:opacity-50',
                deleteImageArmed
                  ? 'text-red-600 font-semibold'
                  : 'text-slate-400 hover:text-red-600'
              )}
            >
              {busy === 'deleteImage'
                ? 'Wird entfernt …'
                : deleteImageArmed
                  ? 'Wirklich? — Ganzes Bild entfernen'
                  : 'Ganzes Bild aus dem Projekt entfernen'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
